import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Skeleton,
  Alert,
  Divider,
} from '@mui/material';
import {
  AccountBalance,
  TrendingUp,
  TrendingDown,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { bankAnalysisAPI } from '../../api';

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);

const monthNames = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const BankStatementSummary = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await bankAnalysisAPI.getSummary();
        setData(res.data.data || []);
      } catch (err) {
        if (err.response?.status !== 404) {
          setError('Failed to load bank statement data');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Skeleton variant="text" width={240} height={32} />
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Skeleton variant="rectangular" width="33%" height={100} />
          <Skeleton variant="rectangular" width="33%" height={100} />
          <Skeleton variant="rectangular" width="33%" height={100} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!data || data.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AccountBalance sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0d2137' }}>
          Bank Statement Summary
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {data.map((stmt, idx) => {
          const label = stmt.bankName
            ? `${stmt.bankName} — ${monthNames[stmt.statementMonth] || ''} ${stmt.statementYear || ''}`
            : `${monthNames[stmt.statementMonth] || ''} ${stmt.statementYear || ''}`;

          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: stmt.cashFlowPositive ? 'success.light' : 'error.light',
                  bgcolor: stmt.cashFlowPositive ? 'success.50' : 'error.50',
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom noWrap>
                  {label}
                </Typography>

                <Divider sx={{ mb: 1.5 }} />

                {/* Deposits */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Deposits
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(stmt.totalDeposits)}
                  </Typography>
                </Box>

                {/* Avg Ledger */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Avg Ledger
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(stmt.averageLedger)}
                  </Typography>
                </Box>

                {/* Cash Flow */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Cash Flow
                  </Typography>
                  <Chip
                    icon={stmt.cashFlowPositive ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                    label={`${stmt.cashFlowPositive ? '+' : ''}${formatCurrency(stmt.cashFlow)}`}
                    color={stmt.cashFlowPositive ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default BankStatementSummary;
